import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(Math.round(v || 0));

const TYPES = [
  {
    key: 'debt',
    icon: '💸',
    color: 'red',
    titleSW: 'Kukumbusha Madeni',
    titleEN: 'Debt Reminders',
    descSW:  'Tuma arifa kwa wanachama wote wanaodaiwa pesa. Kila mwanachama ataona kiasi chake cha deni.',
    descEN:  'Notify all members with outstanding balances. Each member sees their personal debt amount.',
  },
  {
    key: 'deadline',
    icon: '⏰',
    color: 'amber',
    titleSW: 'Ukumbusho wa Muda wa Kulipa',
    titleEN: 'Payment Deadline Reminder',
    descSW:  'Kumbusha wanachama ambao hawajalipia mwezi huu kabla faini haijawekwa.',
    descEN:  'Remind members who haven\'t paid this month before a fine is applied.',
    hasDate: true,
  },
  {
    key: 'statement',
    icon: '📄',
    color: 'blue',
    titleSW: 'Taarifa ya Michango',
    titleEN: 'Contribution Statement',
    descSW:  'Tuma muhtasari wa michango yote kwa kila mwanachama (michango 12 ya hivi karibuni + jumla + hali).',
    descEN:  'Send each member a summary of their contribution history and current balance.',
  },
];

const COLOR = {
  red:   { card: 'border-red-200 bg-red-50',   icon: 'bg-red-100',  btn: 'bg-red-600 hover:bg-red-700',   badge: 'bg-red-100 text-red-700',   title: 'text-red-800',   desc: 'text-red-600' },
  amber: { card: 'border-amber-200 bg-amber-50', icon: 'bg-amber-100', btn: 'bg-amber-600 hover:bg-amber-700', badge: 'bg-amber-100 text-amber-700', title: 'text-amber-800', desc: 'text-amber-600' },
  blue:  { card: 'border-blue-200 bg-blue-50',  icon: 'bg-blue-100',  btn: 'bg-blue-600 hover:bg-blue-700',   badge: 'bg-blue-100 text-blue-700',   title: 'text-blue-800',  desc: 'text-blue-600' },
};

export default function Notifications() {
  const { id } = useParams();
  const { lang } = useLang();
  const isSW = lang === 'sw';

  const [active, setActive]         = useState(null);
  const [channel, setChannel]       = useState('sms');
  const [memberFilter, setMember]   = useState('all');
  const [memberId, setMemberId]     = useState('');
  const [members, setMembers]       = useState([]);
  const [qualifying, setQualifying] = useState([]);
  const [deadlineDate, setDeadline] = useState('');
  const [preview, setPreview]       = useState(null);
  const [loadingPreview, setLP]     = useState(false);
  const [sending, setSending]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const [log, setLog]               = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    api.get(`/members?group_id=${id}`)
      .then(({ data }) => setMembers(Array.isArray(data) ? data : (data.members || data.data || [])))
      .catch(() => {});
  }, [id]);

  const loadLog = () => {
    setLogLoading(true);
    api.get(`/groups/${id}/notify/log`)
      .then(({ data }) => setLog(data.messages || []))
      .catch(() => {})
      .finally(() => setLogLoading(false));
  };

  useEffect(() => { loadLog(); }, [id]);

  const open = (type) => {
    setActive(type);
    setPreview(null);
    setResult(null);
    setError('');
    setMember('all');
    setMemberId('');
    setDeadline('');
    setQualifying([]);
    api.get(`/groups/${id}/notify/preview?type=${type.key}`)
      .then(({ data }) => setQualifying(data.recipients || []))
      .catch(() => {});
  };

  const close = () => {
    setActive(null);
    setPreview(null);
    setResult(null);
    setError('');
  };

  const loadPreview = async () => {
    setLP(true);
    setError('');
    setPreview(null);
    try {
      const params = new URLSearchParams({ type: active.key });
      if (memberFilter === 'single' && memberId) params.set('member_id', memberId);
      if (deadlineDate) params.set('deadline_date', deadlineDate);
      const { data } = await api.get(`/groups/${id}/notify/preview?${params}`);
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Imeshindwa kupakia maelezo.');
    } finally {
      setLP(false);
    }
  };

  const send = async () => {
    if (!preview?.recipients?.length) return;
    setSending(true);
    setError('');
    try {
      const { data } = await api.post(`/groups/${id}/notify`, {
        type: active.key,
        channel,
        member_id: memberFilter === 'single' ? parseInt(memberId) : 0,
        deadline_date: deadlineDate,
      });
      setResult(data);
      setPreview(null);
      loadLog();
    } catch (e) {
      setError(e.response?.data?.error || 'Imeshindwa kutuma taarifa.');
    } finally {
      setSending(false);
    }
  };

  const STATUS_TICKS = {
    sent:      { icon: '✓',  color: 'text-gray-400',    label: 'Imetumwa' },
    delivered: { icon: '✓✓', color: 'text-gray-400',    label: 'Imefika' },
    read:      { icon: '✓✓', color: 'text-blue-500',    label: 'Imesomwa' },
    failed:    { icon: '✕',  color: 'text-red-500',     label: 'Imeshindwa' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={`/groups/${id}`} className="text-green-700 hover:text-green-900 text-sm">
          ← {isSW ? 'Kikundi' : 'Group'}
        </Link>
        <span className="text-gray-400">/</span>
        <h1 className="text-2xl font-bold text-gray-800">
          {isSW ? 'Tuma Taarifa / Arifa' : 'Send Notifications'}
        </h1>
      </div>

      <p className="text-sm text-gray-500">
        {isSW
          ? 'Chagua aina ya taarifa unayotaka kutuma kwa wanachama wa kikundi hiki.'
          : 'Choose the type of notification you want to send to group members.'}
      </p>

      {/* Notification type cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {TYPES.map(t => {
          const c = COLOR[t.color];
          return (
            <div key={t.key} className={`rounded-2xl border p-5 ${c.card}`}>
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${c.icon}`}>
                {t.icon}
              </div>
              <h3 className={`mt-3 font-bold text-base ${c.title}`}>
                {isSW ? t.titleSW : t.titleEN}
              </h3>
              <p className={`mt-1 text-sm ${c.desc}`}>
                {isSW ? t.descSW : t.descEN}
              </p>
              <button
                onClick={() => open(t)}
                className={`mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors ${c.btn}`}
              >
                {isSW ? 'Tuma →' : 'Send →'}
              </button>
            </div>
          );
        })}
      </div>

      {/* WhatsApp delivery/read log (Meta provider only) */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-bold text-gray-800">
            {isSW ? '📬 Historia ya Ujumbe wa WhatsApp' : '📬 WhatsApp Message History'}
          </h2>
          <button onClick={loadLog} disabled={logLoading}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            {logLoading ? '...' : (isSW ? '🔄 Sasisha' : '🔄 Refresh')}
          </button>
        </div>

        {log.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            {isSW ? 'Hakuna ujumbe wa WhatsApp (Meta) uliotumwa bado.' : 'No WhatsApp (Meta) messages sent yet.'}
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {log.map(m => {
              const st = STATUS_TICKS[m.status] || STATUS_TICKS.sent;
              return (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{m.member_name || m.phone}</p>
                    <p className="text-xs text-gray-400">
                      {m.notify_type} · {m.sent_at ? new Date(m.sent_at).toLocaleString('sw-TZ') : '—'}
                    </p>
                  </div>
                  <span className={`shrink-0 flex items-center gap-1 text-sm font-bold ${st.color}`} title={st.label}>
                    {st.icon} <span className="text-xs font-medium">{st.label}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Send modal / panel */}
      {active && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 rounded-t-2xl ${COLOR[active.color].card} border-b`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{active.icon}</span>
                <div>
                  <p className="font-bold text-gray-800">{isSW ? active.titleSW : active.titleEN}</p>
                  <p className="text-xs text-gray-500">{isSW ? 'Kikundi' : 'Group'} #{id}</p>
                </div>
              </div>
              <button onClick={close} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Channel */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {isSW ? 'Njia ya Kutuma' : 'Send Via'}
                </label>
                <div className="flex gap-3">
                  {[['sms','📱 SMS'],['whatsapp','💬 WhatsApp'],['both', isSW ? '📱+💬 Zote' : '📱+💬 Both']].map(([v, l]) => (
                    <button key={v} onClick={() => setChannel(v)}
                      className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-colors ${channel === v ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Member filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {isSW ? 'Wapokeaji' : 'Recipients'}
                </label>
                <div className="flex gap-3">
                  {[['all', isSW ? 'Wote Wanaohusika' : 'All Applicable'], ['single', isSW ? 'Mwanachama Mmoja' : 'Single Member']].map(([v, l]) => (
                    <button key={v} onClick={() => { setMember(v); setMemberId(''); setPreview(null); }}
                      className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-colors ${memberFilter === v ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {memberFilter === 'single' && (
                  <select
                    value={memberId}
                    onChange={e => { setMemberId(e.target.value); setPreview(null); }}
                    className="mt-2 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                  >
                    <option value="">
                      {qualifying.length === 0
                        ? (isSW ? 'Inapakia...' : 'Loading...')
                        : (isSW ? `— Chagua mwanachama (${qualifying.length}) —` : `— Select a member (${qualifying.length}) —`)}
                    </option>
                    {qualifying.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Deadline date (for deadline type) */}
              {active.hasDate && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {isSW ? 'Tarehe ya Mwisho wa Kulipa' : 'Payment Deadline Date'}
                  </label>
                  <input
                    type="date"
                    value={deadlineDate}
                    onChange={e => {
                      const d = e.target.value;
                      setDeadline(d);
                      setPreview(null);
                      setMemberId('');
                      if (d) {
                        setQualifying([]);
                        api.get(`/groups/${id}/notify/preview?type=${active.key}&deadline_date=${d}`)
                          .then(({ data }) => setQualifying(data.recipients || []))
                          .catch(() => {});
                      }
                    }}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              )}

              {/* Preview area */}
              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
              )}

              {preview && (
                <div>
                  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-3 ${COLOR[active.color].badge}`}>
                    {preview.count} {isSW ? 'watakaopokelewa' : 'to receive'}
                  </div>
                  {preview.count === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      {isSW ? 'Hakuna wanachama wanaohusika.' : 'No applicable members found.'}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {preview.recipients.map(r => (
                        <div key={r.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{r.name}</p>
                              <p className="text-xs text-gray-400">{r.phone}</p>
                            </div>
                            {r.debt > 0 && (
                              <span className="text-xs font-bold text-red-600 shrink-0">
                                −{fmt(r.debt)} TZS
                              </span>
                            )}
                          </div>
                          <details className="mt-2">
                            <summary className="text-xs text-blue-600 cursor-pointer select-none">
                              {isSW ? 'Angalia ujumbe' : 'Preview message'}
                            </summary>
                            <pre className="mt-1 whitespace-pre-wrap text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-2">
                              {r.message}
                            </pre>
                          </details>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {result && (
                <div className={`rounded-xl border p-4 ${result.rate_limit ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <p className={`font-semibold mb-3 ${result.rate_limit ? 'text-amber-800' : 'text-green-800'}`}>
                    {result.rate_limit
                      ? (isSW ? '⚠️ Kikwazo cha Kiwango' : '⚠️ Rate Limited')
                      : (isSW ? '✅ Taarifa Zimetumwa!' : '✅ Notifications Sent!')}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-center text-sm mb-3">
                    <div className="bg-green-100 rounded-xl py-2 px-1">
                      <div className="text-xl font-bold text-green-700">{result.whatsapp ?? result.sent}</div>
                      <div className="text-xs text-green-600">💬 WhatsApp</div>
                    </div>
                    {(result.sms_fallback > 0) && (
                      <div className="bg-blue-100 rounded-xl py-2 px-1">
                        <div className="text-xl font-bold text-blue-700">{result.sms_fallback}</div>
                        <div className="text-xs text-blue-600">📱 {isSW ? 'SMS (mbadala)' : 'SMS (fallback)'}</div>
                      </div>
                    )}
                    {(result.failed > 0) && (
                      <div className="bg-red-100 rounded-xl py-2 px-1">
                        <div className="text-xl font-bold text-red-700">{result.failed}</div>
                        <div className="text-xs text-red-600">{isSW ? 'Zilishindwa' : 'Failed'}</div>
                      </div>
                    )}
                    <div className="bg-gray-100 rounded-xl py-2 px-1">
                      <div className="text-xl font-bold text-gray-700">{result.total}</div>
                      <div className="text-xs text-gray-500">{isSW ? 'Jumla' : 'Total'}</div>
                    </div>
                  </div>
                  {result.sms_fallback > 0 && !result.rate_limit && (
                    <p className="text-xs text-blue-700 bg-blue-50 rounded-lg p-2 mb-2">
                      {isSW
                        ? `Wanachama ${result.sms_fallback} hawakupatikana WhatsApp — walitumwa SMS badala yake.`
                        : `${result.sms_fallback} members not on WhatsApp — sent via SMS instead.`}
                    </p>
                  )}
                  {result.rate_limit && (
                    <p className="text-xs text-amber-700 bg-amber-100 rounded-lg p-2">
                      {result.rate_limit}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            {!result && (
              <div className="flex gap-3 px-5 py-4 border-t">
                {!preview ? (
                  <button
                    onClick={loadPreview}
                    disabled={loadingPreview || (memberFilter === 'single' && !memberId)}
                    className="flex-1 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors"
                  >
                    {loadingPreview
                      ? (isSW ? 'Inapakia...' : 'Loading...')
                      : (isSW ? '👁 Angalia Orodha' : '👁 Preview List')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { setPreview(null); setError(''); }}
                      className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                      {isSW ? 'Badilisha' : 'Change'}
                    </button>
                    <button
                      onClick={send}
                      disabled={sending || preview.count === 0}
                      className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50 transition-colors ${COLOR[active.color].btn}`}
                    >
                      {sending
                        ? (isSW ? 'Inatuma...' : 'Sending...')
                        : (isSW ? `📤 Tuma kwa ${preview.count}` : `📤 Send to ${preview.count}`)}
                    </button>
                  </>
                )}
              </div>
            )}
            {result && (
              <div className="px-5 py-4 border-t">
                <button onClick={close}
                  className="w-full rounded-xl bg-gray-800 text-white py-2.5 text-sm font-semibold hover:bg-gray-700">
                  {isSW ? 'Funga' : 'Close'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
